import { useTranslator } from '@bluebeam/react-i18n';
import BaseInput from 'components/inputs/BaseInput';
import DateDropdown from 'components/inputs/dropdowns/DateDropdown';
import SingleSelect from 'components/inputs/dropdowns/SingleSelect';
import Label from 'components/inputs/Label';
import ValidatatableInput from 'components/inputs/ValidatableInput';
import ModalSidebar from 'components/modals/sidebar/ModalSidebar';
import ModalSidebarHeader from 'components/modals/sidebar/ModalSidebarHeader';
import debounce from 'lodash/debounce';
import React, { FC, useEffect, useState } from 'react';
import { PublishRequest, RawPublishRequest } from 'records/PublishRequest';
import { Sheet } from 'records/Sheet';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { useDisciplineSelect } from 'hooks/useDisciplineSelect';

interface Props extends WithAppStoreProps {
  publishRequest: PublishRequest;
}

const PubReqSidebar: FC<Props> = ({ publishRequest, actions, appState }) => {
  const [pr, setPr] = useState(publishRequest);
  const [message, setMessage] = useState('');
  const [isDuplicated, setIsDuplicated] = useState(false);
  const { sheets, publishRequests } = appState;
  const { translate } = useTranslator(['pubReqSidebar', 'disciplines']);
  const { disciplineOptions } = useDisciplineSelect();

  useEffect(() => {
    const isPRDuplicated =
      publishRequests.filter((prr) => prr.sheetnumber === pr.sheetnumber).size >
      1;
    setIsDuplicated(isPRDuplicated);
    let message = sheets.has(pr.sheetnumber)
      ? translate('pubReqSidebar:revision', {
          revisionNumber: sheets.get(pr.sheetnumber, new Sheet()).versions.size,
        })
      : translate('pubReqSidebar:initialUpload');

    if (isPRDuplicated) {
      message = translate('pubReqSidebar:oneVersionPerBatch');
    }

    if (pr.sheetnumber.length === 0) {
      message = translate('pubReqSidebar:sheetNumberRequired');
    }

    setMessage(message);
  }, [pr, publishRequest]);

  const debouncedUpdatePublishRequest = debounce((nextPR) => {
    actions.updatePublishRequest(nextPR);
  }, 1);

  const handleChange = (
    key: keyof RawPublishRequest,
    val: number | string,
    defer = true
  ): void => {
    const nextPR = pr.set(key, val);
    setPr((prevPR) => prevPR.set(key, val));
    if (defer) {
      debouncedUpdatePublishRequest(nextPR);
    } else {
      actions.updatePublishRequest(nextPR);
    }
  };

  const disciplineSelection = React.useMemo(() => {
    return disciplineOptions.has(pr.discipline)
      ? disciplineOptions.get(pr.discipline)
      : null;
  }, [disciplineOptions, pr.discipline]);

  return (
    <ModalSidebar id="pub-req-viewer-modal-sidebar">
      <ModalSidebarHeader title={translate('pubReqSidebar:modalHeader')} />
      <div className="m4">
        <div className="mb2">
          <Label label={translate('pubReqSidebar:number')} required />
          <ValidatatableInput
            id="pub-req-side-panel-number"
            value={pr.sheetnumber}
            isValid={isDuplicated}
            onChange={(evt): void =>
              handleChange('sheetnumber', evt.target.value)
            }
          />
          <div className="py2 font-size-4 color-gray-6">{message}</div>
        </div>
        <div className="mb4">
          <Label label={translate('pubReqSidebar:name')} />
          <BaseInput
            id="pub-req-side-panel-name"
            value={pr.sheettitle}
            onChange={(evt): void =>
              handleChange('sheettitle', evt.target.value)
            }
          />
        </div>
        <div className="mb4">
          <Label label={translate('pubReqSidebar:discipline')} />
          <SingleSelect
            id="pub-req-side-panel-discipline"
            value={disciplineSelection}
            options={Array.from(disciplineOptions.values())}
            onChange={(selectedOption): void =>
              handleChange(
                'discipline',
                selectedOption.value ? selectedOption.value : '',
                false
              )
            }
          />
        </div>
        <div className="mb4">
          <Label label={translate('pubReqSidebar:issuedDate')} />
          <DateDropdown
            id="pub-req-side-panel-issued-date"
            date={pr.issueddate}
            onChange={(date: Date): void => {
              handleChange('issueddate', date.getTime() / 1000);
            }}
          />
        </div>
        <div>
          <Label label={translate('pubReqSidebar:revisionComment')} />
          <BaseInput
            id="pub-req-side-panel-revision-comment"
            value={pr.revisioncomment}
            onChange={(evt): void =>
              handleChange('revisioncomment', evt.target.value)
            }
          />
        </div>
      </div>
    </ModalSidebar>
  );
};

export default withAppStore<Props>(PubReqSidebar);
